import React, { useState } from 'react';
import { Modal, Upload, message, Progress } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { createRecording } from '../Feature/API';
import { getRecordingsThunk, getLabelsThunk } from '../Feature/RecordingsSlice';
import { getLabelsThunk as getLabelsThunkFromLabels } from '../Feature/LabelsSlice';
import uuidv4 from '../Utils/uuid';

export const RecordingUploadModal = ({ visible, onClose, spaceId, dispatch }) => {
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    const handleUpload = async () => {
        setUploading(true);
        try {
            const uploadedFiles = fileList.filter(f => f.status === 'done');
            if (uploadedFiles.length > 0) {
                await dispatch(getLabelsThunkFromLabels(spaceId));
                await dispatch(getRecordingsThunk(spaceId));
                message.success(`${uploadedFiles.length} recording(s) uploaded successfully`);
                setFileList([]);
                onClose();
            }
        } catch (error) {
            message.error('Failed to process uploads: ' + (error.message || 'Unknown error'));
        } finally {
            setUploading(false);
        }
    };

    const props = {
        name: 'file',
        multiple: true,
        beforeUpload(file) {
            const uuid = uuidv4();
            const template = {
                uid: uuid,
                name: file.name,
                status: 'uploading',
                url: null,
                progress: 0,
            };
            setFileList(prev => [...prev, template]);
            setUploadProgress(prev => ({ ...prev, [uuid]: 0 }));

            createRecording(spaceId, file, (percent) => {
                setUploadProgress(prev => ({ ...prev, [uuid]: percent }));
            })
                .then(response => {
                    const done = { status: 'done', url: response.file_uri, progress: 100 };
                    const obj = { ...template, ...done };
                    setFileList(prev => [...prev.filter(song => song.uid !== uuid), obj]);
                })
                .catch(error => {
                    message.error('Failed to upload: ' + (error.message || 'Unknown error'));
                    setFileList(prev => prev.filter(song => song.uid !== uuid));
                    setUploadProgress(prev => {
                        const newProgress = { ...prev };
                        delete newProgress[uuid];
                        return newProgress;
                    });
                });
            return false;
        },
        onDrop(e) {
            console.log('Dropped files', e.dataTransfer.files);
        },
    };

    const handleClose = () => {
        setFileList([]);
        onClose();
    };

    return (
        <Modal
            title="Upload Recordings"
            open={visible}
            onCancel={handleClose}
            onOk={handleUpload}
            confirmLoading={uploading}
            okText="Done"
            width={600}
        >
            <div style={{ marginBottom: '20px' }}>
                <Upload.Dragger {...props} fileList={fileList}>
                    <p className="ant-upload-drag-icon">
                        <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag files to this area to upload</p>
                    <p className="ant-upload-hint">
                        Support for multiple file uploads.
                    </p>
                </Upload.Dragger>
            </div>
            {fileList.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                    <strong>Files:</strong> {fileList.length}
                    {fileList.map(file => (
                        <div key={file.uid} style={{ marginTop: '12px' }}>
                            <div style={{ fontSize: '12px', marginBottom: '4px' }}>
                                {file.name}
                            </div>
                            {file.status === 'uploading' && (
                                <Progress percent={uploadProgress[file.uid] || 0} size="small" />
                            )}
                            {file.status === 'done' && (
                                <div style={{ color: '#52c41a', fontSize: '12px' }}>
                                    ✓ Uploaded
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Modal>
    );
};
